// Voice channel and bark scheduler (rebuild.md §4.8). Every spoken line (barks, generated lines, the Guide's
// primer) goes through one queue, so lines never talk over each other: each starts when the previous one
// ends. Ambient barks are dropped rather than queued (at most one every 8 s, never while anything else is
// speaking). Nothing plays over a running excerpt. Every line shows its subtitle; a setting turns voices off.
import { signal } from '@preact/signals';
import { bark as barkDef } from '@content/barks.ts';
import type { CastId } from '@content/types.ts';
import { audio, type VoiceHandle } from './engine.ts';

export interface Subtitle {
  id: number;
  who: CastId;
  text: string;
}

export const subtitle = signal<Subtitle | null>(null);

const MIN_GAP_MS = 8000;
/** Pause between consecutive lines. */
const LINE_GAP_MS = 300;
/** At most this many lines wait; more are dropped. */
const MAX_QUEUE = 3;
/** A line that has waited this long is dropped: the moment has passed. */
const STALE_MS = 6000;

interface Line {
  who: CastId;
  text: string;
  audio: { kind: 'bark' | 'gen'; id: string } | null;
  /** Cut short as soon as another line is queued (the Guide's primer on the briefing). */
  interruptible?: boolean;
  /** Don't show a subtitle (the text is already on screen). */
  noSubtitle?: boolean;
  onEnd?: () => void;
  queuedAt?: number;
}

const queue: Line[] = [];
let current: { line: Line; stop: () => void } | null = null;
let lastBarkAt = -Infinity;
let excerptPlaying = false;
let voicesOn = true;
let nextId = 1;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

const estimateMs = (text: string) => Math.max(1200, text.length * 70);

function showSubtitle(who: CastId, text: string): number {
  const id = nextId++;
  if (clearTimer) clearTimeout(clearTimer);
  subtitle.value = { id, who, text };
  return id;
}

function hideSubtitleLater(id: number): void {
  if (clearTimer) clearTimeout(clearTimer);
  clearTimer = setTimeout(() => {
    if (subtitle.value?.id === id) subtitle.value = null;
  }, 1200);
}

function start(line: Line): void {
  const subId = line.noSubtitle ? null : showSubtitle(line.who, line.text);
  let finished = false;
  let handle: VoiceHandle | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const done = () => {
    if (finished) return;
    finished = true;
    if (timer) clearTimeout(timer);
    if (current?.line === line) current = null;
    if (subId !== null) hideSubtitleLater(subId);
    line.onEnd?.();
    setTimeout(pump, LINE_GAP_MS);
  };
  current = {
    line,
    stop: () => {
      handle?.stop();
      done();
    },
  };
  const fallback = () => (timer = setTimeout(done, estimateMs(line.text)));
  if (voicesOn && line.audio) {
    void audio.voice(line.audio.kind, line.audio.id).then((h) => {
      if (finished) return h?.stop();
      if (!h) return fallback();
      handle = h;
      void h.ended.then(done);
    });
  } else fallback();
}

function pump(): void {
  if (current || excerptPlaying) return;
  const now = Date.now();
  let next = queue.shift();
  while (next && now - (next.queuedAt ?? now) > STALE_MS) {
    next.onEnd?.();
    next = queue.shift();
  }
  if (next) start(next);
}

function enqueue(line: Line): boolean {
  if (excerptPlaying) return false;
  if (queue.length >= MAX_QUEUE) return false;
  queue.push({ ...line, queuedAt: Date.now() });
  if (current?.line.interruptible) current.stop();
  else pump();
  return true;
}

/** True while a line is playing or waiting. */
export const voiceBusy = (): boolean => !!current || queue.length > 0;

/** Stop the current line and drop everything queued. */
export function stopVoices(): void {
  queue.length = 0;
  current?.stop();
  subtitle.value = null;
}

export function setExcerptPlaying(on: boolean): void {
  excerptPlaying = on;
  if (on) stopVoices();
  else pump();
}

export function setVoicesEnabled(on: boolean): void {
  voicesOn = on;
}

/**
 * Request a bark. Ambient barks are dropped if anything is speaking or one played in the last 8 s;
 * `priority` barks (stage start/complete, AUDITED, scripted beats) wait their turn instead.
 * Returns whether it was played or queued.
 */
export function requestBark(line: string, opts: { priority?: boolean; now?: number } = {}): boolean {
  const now = opts.now ?? Date.now();
  if (excerptPlaying) return false;
  const def = barkDef(line);
  if (!def) return false;
  if (!opts.priority && (voiceBusy() || now - lastBarkAt < MIN_GAP_MS)) return false;
  lastBarkAt = now;
  return enqueue({ who: def.speaker, text: def.text, audio: { kind: 'bark', id: line } });
}

/** A scripted line: queued after anything already speaking; voiced if a generated take exists. */
export function requestLine(who: CastId, text: string, genId?: string): boolean {
  return enqueue({ who, text, audio: genId ? { kind: 'gen', id: genId } : null });
}

/** The Guide's primer on the briefing: no subtitle, and cut short as soon as the stage speaks. */
export function playPrimer(genId: string, onEnd: () => void): boolean {
  return enqueue({ who: 'guide', text: '', audio: { kind: 'gen', id: genId }, interruptible: true, noSubtitle: true, onEnd });
}

/** Test hook. */
export function resetBarkScheduler(): void {
  queue.length = 0;
  current = null;
  lastBarkAt = -Infinity;
  excerptPlaying = false;
  subtitle.value = null;
}

/** Stop the primer (leaving the briefing, or a second tap on its button). */
export function stopPrimer(): void {
  for (let i = queue.length - 1; i >= 0; i--) if (queue[i]!.interruptible) queue.splice(i, 1);
  if (current?.line.interruptible) current.stop();
}
